import { useEffect } from "react";

type JsonLd = Record<string, unknown> | Record<string, unknown>[];

type SEOProps = {
  title: string;
  description: string;
  canonicalPath: string;
  keywords?: string;
  jsonLd?: JsonLd;
  socialImage?: string;
};

const SITE_URL = "https://privacy360.io";
const DEFAULT_SOCIAL_IMAGE = `${SITE_URL}/og-image.jpg`;

const upsertMeta = (selector: string, attr: "name" | "property", key: string, content: string) => {
  let tag = document.head.querySelector<HTMLMetaElement>(selector);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
};

const SEO = ({ title, description, canonicalPath, keywords, jsonLd, socialImage }: SEOProps) => {
  useEffect(() => {
    // Always emit canonical with trailing slash so it matches Cloudflare's 308 target.
    const normalizedPath =
      canonicalPath === "/" ? "/" : canonicalPath.endsWith("/") ? canonicalPath : `${canonicalPath}/`;
    const canonicalUrl = `${SITE_URL}${normalizedPath}`;
    const socialImageUrl = socialImage
      ? socialImage.startsWith("http")
        ? socialImage
        : `${SITE_URL}${socialImage.startsWith("/") ? socialImage : `/${socialImage}`}`
      : DEFAULT_SOCIAL_IMAGE;

    document.title = title;
    upsertMeta('meta[name="description"]', "name", "description", description);
    if (keywords) {
      upsertMeta('meta[name="keywords"]', "name", "keywords", keywords);
    }
    upsertMeta('meta[property="og:title"]', "property", "og:title", title);
    upsertMeta('meta[property="og:description"]', "property", "og:description", description);
    upsertMeta('meta[property="og:url"]', "property", "og:url", canonicalUrl);
    upsertMeta('meta[property="og:image"]', "property", "og:image", socialImageUrl);
    upsertMeta('meta[property="og:image:alt"]', "property", "og:image:alt", title);
    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
    upsertMeta('meta[name="twitter:image"]', "name", "twitter:image", socialImageUrl);

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;

    document.querySelectorAll('script[type="application/ld+json"][data-seo-json-ld="true"]').forEach((script) => script.remove());

    if (jsonLd) {
      const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      items.forEach((item) => {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.dataset.seoJsonLd = "true";
        script.textContent = JSON.stringify({
          "@context": "https://schema.org",
          ...(item as Record<string, unknown>),
        });
        document.head.appendChild(script);
      });
    }
  }, [canonicalPath, description, jsonLd, keywords, socialImage, title]);

  return null;
};

export { SITE_URL };
export default SEO;